import React, { useContext } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import { DialogOverlay, DialogContent } from "@reach/dialog"
import LoadingAnimation from "../loading-animation"
import { IconButton } from "../button"
import { PortalNode } from "../portal"
import { motion, AnimatePresence } from "framer-motion"
import ConfirmModal from "./confirm-modal"
import confirm from "./confirm"
import Action, { ActionProvider, Actions, ActionContext } from "./action"
import TitleBar from "./title-bar"
import { useMediaBreakpoint } from "../use-media-breakpoint"
import { useModalTelemetry } from "./use-modal-telemetry"

const AnimatedDialogContent = motion.create(DialogContent)

const DEFAULT = "p-0 my-auto bg-white font-sans shadow max-h-screen"
const HEADER_DEFAULT = "flex items-center justify-between px-6"

const SIZE_CLASSES = {
  md: "max-w-2xl w-4/5",
  lg: "max-w-4xl w-4/5",
  full: "max-w-full w-4/5",
  "new-full": "max-w-full flex flex-col",
  fullscreen: "w-screen h-screen"
}

const SIZE_STYLES = {
  md: {},
  lg: {},
  full: {
    maxWidth: "1600px",
    width: "calc(100vw - 4rem)",
    margin: "4rem auto 4rem auto"
  },
  "new-full": options => ({
    boxSizing: "border-box",
    width: "auto",
    margin: options.md ? "2.5rem" : "0.5rem",
    height: options.md ? "calc(100dvh - 5rem)" : "calc(100dvh - 1rem)", // margin top + margin bottom from the margin above
    overflow: "hidden"
  }),
  fullscreen: {
    margin: 0
  }
}

function getStyles(size, options) {
  if (typeof SIZE_STYLES[size] === "function") {
    return SIZE_STYLES[size](options)
  }

  return SIZE_STYLES[size]
}

function Header({ children, className, separator }) {
  const { actions } = useContext(ActionContext)

  const classes = cx(HEADER_DEFAULT, className, {
    "border-b border-gray-200 py-3 md:py-5": actions.length > 0 || separator,
    "pt-6 pb-2": actions.length === 0 && !separator
  })

  return <div className={classes}>{children}</div>
}

export default function Modal({
  children,
  label,
  ariaLabel,
  onClose,
  className,
  size = "md",
  loading = false,
  width,
  hideCloseButton = false,
  hideHeader = false,
  separator,
  telemetry,
  fullBleed = false,
  ...props
}) {
  const md = useMediaBreakpoint("md")
  const styles = getStyles(size, { md })

  useModalTelemetry({ label, ariaLabel, telemetry })

  return (
    <ActionProvider>
      <DialogOverlay
        onDismiss={onClose}
        className={
          ["full", "fullscreen", "new-full"].includes(size)
            ? ""
            : "flex justify-center items-center pt-8"
        }
        {...props}
      >
        <AnimatePresence>
          <AnimatedDialogContent
            key="modal-content"
            aria-label={ariaLabel || label}
            className={cx(
              className,
              DEFAULT,
              SIZE_CLASSES[size],
              !fullBleed && "rounded-lg"
            )}
            style={{ ...styles, maxWidth: width }}
            initial={{
              opacity: 0,
              y: size === "full" || size === "new-full" ? 15 : -15
            }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {!hideHeader && (
              <Header className="gap-8" separator={separator}>
                {label ? <TitleBar content={label} /> : <div />}
                {!hideCloseButton && (
                  <IconButton
                    icon="close"
                    is="tertiary"
                    className="focus:outline-none hover:bg-gray-50"
                    label="Close"
                    onClick={onClose}
                    telemetry={false}
                  />
                )}
              </Header>
            )}
            {loading ? (
              <div className="flex justify-center">
                <LoadingAnimation />
              </div>
            ) : (
              <div
                className={cx("bg-white h-full", {
                  "overflow-hidden": size === "new-full",
                  "rounded-b-lg": !fullBleed,
                  "rounded-t-lg": !fullBleed && hideHeader
                })}
              >
                <PortalNode
                  className={cx("bg-white h-full", {
                    "p-3 md:p-6": !fullBleed,
                    "rounded-b-lg": !fullBleed,
                    "rounded-t-lg": !fullBleed && hideHeader
                  })}
                >
                  {children}
                </PortalNode>
                <Actions />
              </div>
            )}
          </AnimatedDialogContent>
        </AnimatePresence>
      </DialogOverlay>
    </ActionProvider>
  )
}

Modal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  /** Typically a string, but you can use JSX for more customizability. */
  label: PropTypes.node,
  /** A label that describes the modal on screen readers. Add this if you don't provide a label, or if you provide a JSX label. */
  ariaLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** You don't need to provide this if you set a `width`. */
  size: PropTypes.oneOf([
    "md",
    "lg",
    "full",
    "fullscreen",
    "custom",
    "new-full"
  ]),
  hideCloseButton: PropTypes.bool,
  /** Hides the header */
  hideHeader: PropTypes.bool,
  /** forces the separator under the title even when there are no Actions */
  separator: PropTypes.bool,
  /** Data to emit alongside the "modal_open" and "modal_close" events. Set to `false` to disable emission. */
  telemetry: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([false])]),
  verticalButtons: PropTypes.bool,
  /**
   * When true, removes padding and rounded corners for full-bleed/edge-to-edge content.
   * @default false
   */
  fullBleed: PropTypes.bool
}

export { Action, ConfirmModal, confirm }
